/**
 * Indian currency display for upcoming projects (Thousand / Lakh / Crore).
 * Uses absolute rupee amounts stored in `price`, or typed strings with units.
 */

const trimTrailingZeros = (value) => {
  const fixed = Number(value).toFixed(2);
  return fixed.replace(/\.?0+$/, '');
};

const detectPriceUnit = (text) => {
  const lower = String(text || '').toLowerCase();
  if (lower.includes('lakh') || lower.includes('lac')) return 'lakh';
  if (lower.includes('crore') || /(^|[^a-z])cr([^a-z]|$)/i.test(lower)) return 'crore';
  if (lower.includes('thousand')) return 'thousand';
  return null;
};

/**
 * Parse a price string that may include units (Lakh, Cr, Thousand) into rupees.
 * @param {string|number} raw
 * @returns {number}
 */
export const parseIndianPriceToRupees = (raw) => {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;

  const text = String(raw).trim();
  if (!text) return 0;

  const clean = text.replace(/[^\d.]/g, '');
  let num = parseFloat(clean) || 0;
  if (!num) return 0;

  const unit = detectPriceUnit(text);
  if (unit === 'lakh') num *= 100000;
  else if (unit === 'crore') num *= 10000000;
  else if (unit === 'thousand') num *= 1000;

  return num;
};

const formatByUnit = (num, unit, options = {}) => {
  const { short = false } = options;
  if (!num) return '';

  const croreLabel = short ? 'Cr' : 'Crore';

  if (unit === 'crore') {
    return `₹${trimTrailingZeros(num / 10000000)} ${croreLabel}`;
  }
  if (unit === 'lakh') {
    return `₹${trimTrailingZeros(num / 100000)} Lakh`;
  }
  if (unit === 'thousand') {
    return `₹${trimTrailingZeros(num / 1000)} Thousand`;
  }

  // No typed unit — pick by magnitude (same as add/edit preview)
  if (num >= 10000000) {
    return `₹${trimTrailingZeros(num / 10000000)} ${croreLabel}`;
  }
  if (num >= 100000) {
    return `₹${trimTrailingZeros(num / 100000)} Lakh`;
  }
  if (num >= 1000) {
    return `₹${trimTrailingZeros(num / 1000)} Thousand`;
  }
  return `₹${num}`;
};

const formatSingleAmount = (raw, options = {}) => {
  const text = String(raw ?? '').trim();
  if (!text) return '';

  const unit = detectPriceUnit(text);
  const num = parseIndianPriceToRupees(text);
  if (!num) return '';

  return formatByUnit(num, unit, options);
};

/**
 * Format rupees / typed price. If a unit was typed (Lakh/Cr/Thousand), keep that unit.
 * @param {string|number} raw
 * @param {{ short?: boolean }} options
 * @returns {string} e.g. "₹45 Lakh" or "₹1.5 Cr"
 */
export const formatIndianPriceDisplay = (raw, options = {}) => {
  if (raw == null || raw === '') return '';
  const text = String(raw).trim();
  if (!text) return '';

  if (/[-–—]|to/i.test(text)) {
    return formatIndianPriceRangeDisplay(text, options);
  }

  return formatSingleAmount(text, options);
};

/**
 * Prefill edit form: numeric value + unit matching magnitude (not always Cr).
 * @param {string|number} priceInRupees
 * @returns {string} e.g. "45 Lakh", "1.5 Cr", "50 Thousand"
 */
export const formatIndianPriceForEditInput = (priceInRupees) => {
  const num = parseFloat(priceInRupees);
  if (!num || Number.isNaN(num)) return '';

  if (num >= 10000000) {
    return `${trimTrailingZeros(num / 10000000)} Cr`;
  }
  if (num >= 100000) {
    return `${trimTrailingZeros(num / 100000)} Lakh`;
  }
  if (num >= 1000) {
    return `${trimTrailingZeros(num / 1000)} Thousand`;
  }
  return String(num);
};

/**
 * Format a range string ("45-60" or "45 Lakh - 60 Lakh") for display.
 * Preserves typed units on each side when present.
 */
export const formatIndianPriceRangeDisplay = (raw, options = {}) => {
  if (raw == null || raw === '') return '';
  const text = String(raw).trim();
  if (!text) return '';

  if (/[-–—]|to/i.test(text)) {
    const parts = text.split(/[-–—]|to/i).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 2) {
      // If only the second part has a unit (e.g. "45-60 Lakh"), apply it to both
      const sharedUnit = detectPriceUnit(parts[1]) || detectPriceUnit(parts[0]);
      const unitLabel =
        sharedUnit === 'crore' ? 'Cr' : sharedUnit === 'lakh' ? 'Lakh' : sharedUnit === 'thousand' ? 'Thousand' : '';
      const startRaw = detectPriceUnit(parts[0])
        ? parts[0]
        : sharedUnit
          ? `${parts[0]} ${unitLabel}`
          : parts[0];
      const endRaw = parts[1];
      const start = formatSingleAmount(startRaw, options);
      const end = formatSingleAmount(endRaw, options);
      if (start && end) return `${start} - ${end}`;
    }
  }

  return formatSingleAmount(text, options);
};

/**
 * Project details / cards price display.
 * Uses Thousand / Lakh / Crore by amount — never shows 0.10 Cr for 10 Lakh.
 */
export const formatProjectStartingPriceDisplay = (startingPrice, priceInRupees, options = {}) => {
  const { short = true } = options;
  const typed = startingPrice != null && String(startingPrice).trim() !== ''
    ? String(startingPrice).trim()
    : '';

  let absolute = parseFloat(String(priceInRupees ?? '').replace(/,/g, ''));
  if (!Number.isFinite(absolute) || absolute <= 0) {
    absolute = parseIndianPriceToRupees(typed);
  }

  if (!Number.isFinite(absolute) || absolute <= 0) {
    return formatIndianPriceDisplay(typed, options) || '';
  }

  // Ranges in typed starting price (e.g. "45-60 Lakh")
  if (typed && /[-–—]|to/i.test(typed)) {
    const formatted = formatIndianPriceRangeDisplay(typed, options);
    // If range was wrongly all-Cr for sub-crore values, fall back to magnitude of absolute
    if (formatted && !/\b0\.\d+\s*Cr\b/i.test(formatted)) {
      return formatted;
    }
  }

  const unit = detectPriceUnit(typed);
  const croreLabel = short ? 'Cr' : 'Crore';

  if (unit === 'lakh') {
    return `₹${trimTrailingZeros(absolute / 100000)} Lakh`;
  }
  if (unit === 'thousand') {
    return `₹${trimTrailingZeros(absolute / 1000)} Thousand`;
  }

  // Cr, plain number, or missing — pick unit by magnitude
  if (absolute >= 10000000) {
    return `₹${trimTrailingZeros(absolute / 10000000)} ${croreLabel}`;
  }
  if (absolute >= 100000) {
    return `₹${trimTrailingZeros(absolute / 100000)} Lakh`;
  }
  if (absolute >= 1000) {
    return `₹${trimTrailingZeros(absolute / 1000)} Thousand`;
  }
  return `₹${absolute}`;
};

/**
 * Prefill starting price input on edit.
 * Always shows natural unit from rupees (10 Lakh — never 0.10 Cr).
 */
export const resolveEditStartingPriceInput = (typedStartingPrice, priceInRupees) => {
  const typed = typedStartingPrice != null ? String(typedStartingPrice).trim() : '';

  let absolute = parseFloat(String(priceInRupees ?? '').replace(/,/g, ''));
  if (!Number.isFinite(absolute) || absolute <= 0) {
    absolute = parseIndianPriceToRupees(typed);
  }

  if (!Number.isFinite(absolute) || absolute <= 0) {
    return typed;
  }

  const unit = detectPriceUnit(typed);

  // Keep Lakh / Thousand if that is what was typed
  if (unit === 'lakh') {
    return `${trimTrailingZeros(absolute / 100000)} Lakh`;
  }
  if (unit === 'thousand') {
    return `${trimTrailingZeros(absolute / 1000)} Thousand`;
  }

  // Plain number, Cr, or missing unit → pick by magnitude (fixes 0.10 Cr → 10 Lakh)
  return formatIndianPriceForEditInput(absolute);
};
